import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Checkbox,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Autocomplete,
  IconButton,
} from '@mui/material';
import { CloudUpload, Delete } from '@mui/icons-material';
import api from '../../services/api';
import SwalWithA11yFix from '../../services/swalConfig';

const formatDateInput = (value) => {
  if (!value) return '';
  // Value is expected in YYYY-MM-DD from <input type="date">
  return value;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeEmailEntries = (entries = []) => {
  const splitEntries = entries
    .flatMap((entry) => String(entry || '').split(','))
    .map((entry) => entry.trim().toLowerCase())
    .filter(Boolean);

  return Array.from(new Set(splitEntries));
};

const DirectReportSharing = () => {
  const fileInputRef = useRef(null);

  const [sending, setSending] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);
  const [reportsError, setReportsError] = useState('');
  const [reports, setReports] = useState([]);

  const [practitionerQuery, setPractitionerQuery] = useState('');
  const [patientQuery, setPatientQuery] = useState('');

  const [practitionerOptions, setPractitionerOptions] = useState([]);
  const [patientOptions, setPatientOptions] = useState([]);

  const [loadingPractitioners, setLoadingPractitioners] = useState(false);
  const [loadingPatients, setLoadingPatients] = useState(false);

  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const [patientName, setPatientName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [file, setFile] = useState(null);
  const [sendViaEmail, setSendViaEmail] = useState(false);
  const [notifySelectedPractitioner, setNotifySelectedPractitioner] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [emailError, setEmailError] = useState('');
  const [emailInputValue, setEmailInputValue] = useState('');
  const [autoAddedPractitionerEmail, setAutoAddedPractitionerEmail] = useState('');

  const effectivePatientName = useMemo(() => {
    if (patientName.trim()) return patientName.trim();
    if (selectedPatient) return `${selectedPatient.firstName || ''} ${selectedPatient.lastName || ''}`.trim();
    return '';
  }, [patientName, selectedPatient]);

  useEffect(() => {
    let didCancel = false;
    const handle = setTimeout(async () => {
      setLoadingPractitioners(true);
      try {
        const res = await api.get('/admin/users', {
          params: { role: 'practitioner', q: practitionerQuery.trim() || undefined, isActive: 'true' },
        });
        if (!didCancel) setPractitionerOptions(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (!didCancel) setPractitionerOptions([]);
      } finally {
        if (!didCancel) setLoadingPractitioners(false);
      }
    }, 250);
    return () => {
      didCancel = true;
      clearTimeout(handle);
    };
  }, [practitionerQuery]);

  useEffect(() => {
    let didCancel = false;
    const handle = setTimeout(async () => {
      setLoadingPatients(true);
      try {
        const res = await api.get('/admin/users', {
          params: { role: 'patient', q: patientQuery.trim() || undefined, isActive: 'true' },
        });
        if (!didCancel) setPatientOptions(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (!didCancel) setPatientOptions([]);
      } finally {
        if (!didCancel) setLoadingPatients(false);
      }
    }, 250);
    return () => {
      didCancel = true;
      clearTimeout(handle);
    };
  }, [patientQuery]);

  useEffect(() => {
    // If admin selects a patient, prefill patient name (DOB is still required manually)
    if (selectedPatient) {
      setPatientName(`${selectedPatient.firstName || ''} ${selectedPatient.lastName || ''}`.trim());
    }
  }, [selectedPatient]);

  useEffect(() => {
    if (!sendViaEmail || !notifySelectedPractitioner) {
      if (autoAddedPractitionerEmail) {
        setEmailRecipients((prev) => prev.filter((email) => email !== autoAddedPractitionerEmail));
        setAutoAddedPractitionerEmail('');
      }
      return;
    }

    const practitionerEmail = String(selectedPractitioner?.email || '').trim().toLowerCase();
    if (!practitionerEmail) return;

    setEmailRecipients((prev) => normalizeEmailEntries([...prev, practitionerEmail]));
    setAutoAddedPractitionerEmail(practitionerEmail);
  }, [sendViaEmail, notifySelectedPractitioner, selectedPractitioner, autoAddedPractitionerEmail]);

  const resetForm = () => {
    setSelectedPractitioner(null);
    setSelectedPatient(null);
    setPatientName('');
    setDateOfBirth('');
    setFile(null);
    setSendViaEmail(false);
    setNotifySelectedPractitioner(false);
    setEmailRecipients([]);
    setEmailError('');
    setEmailInputValue('');
    setAutoAddedPractitionerEmail('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleEmailRecipientsChange = (_, value) => {
    const normalizedEmails = normalizeEmailEntries(value);
    const invalidEmails = normalizedEmails.filter((email) => !EMAIL_REGEX.test(email));
    setEmailRecipients(normalizedEmails);
    setEmailError(invalidEmails.length ? `Invalid email${invalidEmails.length > 1 ? 's' : ''}: ${invalidEmails.join(', ')}` : '');
  };

  const fetchRecentReports = async () => {
    setReportsError('');
    setLoadingReports(true);
    try {
      const res = await api.get('/admin/direct-reports');
      setReports(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setReportsError(e?.response?.data?.message || 'Failed to load direct reports.');
      setReports([]);
    } finally {
      setLoadingReports(false);
    }
  };

  useEffect(() => {
    fetchRecentReports();
  }, []);

  const deleteReport = async (reportId) => {
    const result = await SwalWithA11yFix({
      icon: 'warning',
      title: 'Delete this report?',
      text: 'This will remove it from the practitioner dashboard. This action is recorded for audit history.',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d32f2f',
    });
    if (!result.isConfirmed) return;

    try {
      await api.delete(`/admin/direct-reports/${reportId}`);
      setReports((prev) => (prev || []).filter((r) => r._id !== reportId));
      await SwalWithA11yFix({
        icon: 'success',
        title: 'Deleted',
        text: 'Report deleted.',
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (e) {
      const message = e?.response?.data?.message || 'Failed to delete report.';
      await SwalWithA11yFix({ icon: 'error', title: 'Delete failed', text: message });
    }
  };

  const sendReport = async () => {
    if (!selectedPractitioner?._id) {
      await SwalWithA11yFix({ icon: 'warning', title: 'Missing practitioner', text: 'Please select a practitioner.' });
      return;
    }
    if (!effectivePatientName) {
      await SwalWithA11yFix({ icon: 'warning', title: 'Missing patient name', text: 'Please enter the patient name.' });
      return;
    }
    if (!dateOfBirth) {
      await SwalWithA11yFix({ icon: 'warning', title: 'Missing date of birth', text: 'Please select the date of birth.' });
      return;
    }
    if (!file) {
      await SwalWithA11yFix({ icon: 'warning', title: 'Missing file', text: 'Please choose a PDF/JPG/PNG report file.' });
      return;
    }
    if (sendViaEmail) {
      if (!emailRecipients.length) {
        await SwalWithA11yFix({
          icon: 'warning',
          title: 'Missing email recipients',
          text: 'Please enter at least one recipient email address.',
        });
        return;
      }
      const invalidEmails = emailRecipients.filter((email) => !EMAIL_REGEX.test(email));
      if (invalidEmails.length) {
        setEmailError(`Invalid email${invalidEmails.length > 1 ? 's' : ''}: ${invalidEmails.join(', ')}`);
        await SwalWithA11yFix({
          icon: 'warning',
          title: 'Invalid email addresses',
          text: 'Please correct invalid email values before sending.',
        });
        return;
      }
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('practitionerId', selectedPractitioner._id);
    formData.append('patientName', effectivePatientName);
    formData.append('dateOfBirth', formatDateInput(dateOfBirth));
    formData.append('sendViaEmail', String(sendViaEmail));
    formData.append('notifySelectedPractitioner', String(notifySelectedPractitioner));
    if (sendViaEmail && emailRecipients.length) {
      formData.append('recipientEmails', JSON.stringify(emailRecipients));
    }

    setSending(true);
    try {
      const response = await api.post('/admin/direct-reports', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const emailDelivery = response?.data?.emailDelivery;
      const emailFailed = emailDelivery && emailDelivery.sent === false;

      await SwalWithA11yFix({
        icon: emailFailed ? 'warning' : 'success',
        title: emailFailed ? 'Uploaded with email issue' : 'Sent',
        text: emailFailed
          ? `Report uploaded, but email delivery failed: ${emailDelivery.reason || 'Unknown error'}.`
          : 'Report uploaded and sent to the practitioner dashboard.',
        timer: emailFailed ? undefined : 1600,
        showConfirmButton: emailFailed,
      });
      resetForm();
      await fetchRecentReports();
    } catch (e) {
      const message = e?.response?.data?.message || e?.response?.data?.errors?.[0]?.msg || 'Failed to send report.';
      await SwalWithA11yFix({ icon: 'error', title: 'Send failed', text: message });
    } finally {
      setSending(false);
    }
  };

  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          Direct Report Sharing
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload a report and send it directly to a practitioner without creating an order.
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              value={selectedPractitioner}
              options={practitionerOptions}
              onChange={(_, value) => setSelectedPractitioner(value)}
              inputValue={practitionerQuery}
              onInputChange={(_, value) => setPractitionerQuery(value)}
              loading={loadingPractitioners}
              getOptionLabel={(opt) =>
                opt ? `${opt.firstName || ''} ${opt.lastName || ''}`.trim() || opt.email || '' : ''
              }
              isOptionEqualToValue={(opt, val) => opt?._id === val?._id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search practitioner"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingPractitioners ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              value={selectedPatient}
              options={patientOptions}
              onChange={(_, value) => setSelectedPatient(value)}
              inputValue={patientQuery}
              onInputChange={(_, value) => setPatientQuery(value)}
              loading={loadingPatients}
              getOptionLabel={(opt) => (opt ? `${opt.firstName || ''} ${opt.lastName || ''}`.trim() || opt.email || '' : '')}
              isOptionEqualToValue={(opt, val) => opt?._id === val?._id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search patient (optional)"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingPatients ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Patient Name"
              size="small"
              fullWidth
              value={patientName}
              onChange={(e) => setPatientName(e.target.value)}
              placeholder="Enter patient full name"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Date of Birth"
              size="small"
              type="date"
              fullWidth
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendViaEmail}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setSendViaEmail(enabled);
                    if (!enabled) {
                      setNotifySelectedPractitioner(false);
                      setEmailRecipients([]);
                      setEmailError('');
                      setEmailInputValue('');
                      setAutoAddedPractitionerEmail('');
                    }
                  }}
                />
              }
              label="Send via Email"
              sx={{ mb: sendViaEmail ? 1 : 0 }}
            />

            {sendViaEmail && (
              <Box sx={{ maxWidth: { xs: '100%', md: '70%' } }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={notifySelectedPractitioner}
                      onChange={(e) => setNotifySelectedPractitioner(e.target.checked)}
                    />
                  }
                  label="Notify Selected Practitioner"
                  sx={{ mb: 0.5 }}
                />
                <Autocomplete
                  multiple
                  freeSolo
                  value={emailRecipients}
                  inputValue={emailInputValue}
                  options={[]}
                  onChange={handleEmailRecipientsChange}
                  onInputChange={(_, value) => setEmailInputValue(value)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => <Chip variant="outlined" label={option} {...getTagProps({ index })} key={option} />)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Recipient Emails"
                      placeholder="Enter one or more emails"
                      onKeyDown={(event) => {
                        if (event.key !== ',') return;
                        event.preventDefault();
                        const normalized = normalizeEmailEntries([...emailRecipients, emailInputValue]);
                        setEmailRecipients(normalized);
                        const invalidEmails = normalized.filter((email) => !EMAIL_REGEX.test(email));
                        setEmailError(
                          invalidEmails.length
                            ? `Invalid email${invalidEmails.length > 1 ? 's' : ''}: ${invalidEmails.join(', ')}`
                            : ''
                        );
                        setEmailInputValue('');
                      }}
                      error={Boolean(emailError)}
                      helperText={emailError || 'Press Enter or type comma to add multiple email addresses.'}
                    />
                  )}
                />
              </Box>
            )}
          </Grid>

          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button component="label" variant="outlined" size="small" startIcon={<CloudUpload />} disabled={sending}>
                {file ? 'Change File' : 'Choose File'}
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </Button>
              <Typography variant="body2" color="text.secondary">
                {file ? file.name : 'PDF / JPG / PNG (max 10MB)'}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
              <Button variant="outlined" size="small" onClick={resetForm} disabled={sending}>
                Reset
              </Button>
              <Button variant="contained" size="small" onClick={sendReport} disabled={sending}>
                {sending ? 'Sending…' : 'Upload & Send'}
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Recent Direct Reports
          </Typography>
          <Button variant="outlined" size="small" onClick={fetchRecentReports} disabled={loadingReports}>
            Refresh
          </Button>
        </Box>

        {reportsError && (
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
            {reportsError}
          </Typography>
        )}

        {loadingReports ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={22} />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Patient</TableCell>
                  <TableCell>DOB</TableCell>
                  <TableCell>Practitioner</TableCell>
                  <TableCell>Uploaded</TableCell>
                  <TableCell>Download Status</TableCell>
                  <TableCell>Downloaded At</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(reports || []).map((r) => {
                  const isDownloaded = Boolean(r.practitionerDownloadedAt);
                  const practitionerName = r.practitioner
                    ? `${r.practitioner.firstName || ''} ${r.practitioner.lastName || ''}`.trim()
                    : '-';
                  return (
                    <TableRow key={r._id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {r.patientName || '-'}
                        </Typography>
                      </TableCell>
                      {/* <TableCell>{r.dateOfBirth ? new Date(r.dateOfBirth).toLocaleDateString() : '-'}</TableCell> */}
                      <TableCell>
                        {r.dateOfBirth
                          ? (() => {
                              const d = new Date(r.dateOfBirth);
                              const mm = String(d.getMonth() + 1).padStart(2, '0');
                              const dd = String(d.getDate()).padStart(2, '0');
                              const yyyy = d.getFullYear();
                              return `${mm}/${dd}/${yyyy}`;
                            })()
                          : '-'}
                      </TableCell>
                      <TableCell>{practitionerName || r.practitioner?.email || '-'}</TableCell>
                      <TableCell>{r.createdAt ? new Date(r.createdAt).toLocaleString() : '-'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={isDownloaded ? 'Downloaded' : 'Not Downloaded'}
                          color={isDownloaded ? 'success' : 'default'}
                          variant={isDownloaded ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>{r.practitionerDownloadedAt ? new Date(r.practitionerDownloadedAt).toLocaleString() : '-'}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" color="error" onClick={() => deleteReport(r._id)} title="Delete report">
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default DirectReportSharing;

